import { useEffect, PropsWithChildren } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { REDIRECT_QUERY_STRING_PARAM } from '@api/base';
import { useCheckSession } from '@hooks';
import { LOGIN_PATH } from '@utils/constants/Routes';
import { generateInfoToken } from '@utils/redirect';
import CircularLoading from '@components/loading';
import styles from './route.module.scss';

interface RouteProps {
    anonymous?: boolean;
}

const ProtectedRoute = ({ anonymous, children }: PropsWithChildren<RouteProps>) => {
    const { account, accountChecked } = useCheckSession(anonymous);
    const navigate = useNavigate();
    const location = useLocation();

    // Effect to check if the user is authenticated or not in case the
    // application is rendering a page that needs it.
    useEffect(() => {
        const isValidSession = account?.token && account?.verified && !account?.banned;

        // The authentication was already checked.
        if (accountChecked && isValidSession) {
            return;
        }

        // The page does not need any authentication.
        if (anonymous) {
            return;
        }

        // The page needs authentication but is invalid.
        if (!anonymous && accountChecked && !isValidSession) {
            const redirectPath = generateInfoToken(location);
            navigate(`${LOGIN_PATH}?${REDIRECT_QUERY_STRING_PARAM}=${redirectPath}`, {
                replace: true,
            });
        }
    }, [anonymous, accountChecked, navigate, location, account]);

    if (!anonymous && (!accountChecked || !account)) {
        return (
            <div className={styles['route__loading-container']}>
                <CircularLoading />
            </div>
        );
    }

    return <>{children}</>;
};

export default ProtectedRoute;
